import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  UpdatePriceChangeSubmissionFailure,
  UpdatePriceChangeSubmissionInput,
  UpdatePriceChangeSubmissionMutation,
} from "@/graphql/codegen/graphql"
import { UpdatePriceChangeSubmissionDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseUpdatePriceChangeSubmissionReturn {
  updatePriceChangeSubmission: (input: UpdatePriceChangeSubmissionInput) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdatePriceChangeSubmission = (): UseUpdatePriceChangeSubmissionReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, updatePriceChangeMutation] = useMutation<
    UpdatePriceChangeSubmissionMutation,
    { input: UpdatePriceChangeSubmissionInput }
  >(UpdatePriceChangeSubmissionDocument)

  const updatePriceChangeSubmission = useCallback(
    async (input: UpdatePriceChangeSubmissionInput): Promise<boolean> => {
      const { data, error } = await updatePriceChangeMutation({ input })
      const output = data?.priceChange.updatePriceChangeSubmission
      const hasError = error || output?.__typename === "UpdatePriceChangeSubmissionFailure"

      if (hasError) {
        const message = error?.message ?? (output as UpdatePriceChangeSubmissionFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }
      notify({ type: NotificationType.Success, message: "Price changes updated" })
      return true
    },
    [updatePriceChangeMutation, notify]
  )

  return { updatePriceChangeSubmission, isUpdating: fetching }
}
